import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import star from '~/assets/icons/star_exp.svg';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import {
	StyledContainer,
	StyledLevelContainer,
	StyledLevelProgress,
	StyledLevelProgressContainer,
	StyledLevelProgressWrapper,
	StyledLevelSquare,
} from './XPInfo.styled';

const XPInfo = () => {
	const playerLevel = useObservable(rage.player.getPlayerLevel());
	const level = playerLevel?.level ?? 0;
	const levelProgress = playerLevel?.levelProgress ?? 0;

	const earnedExp = useObservable(rage.player.getLastEarnedExp());

	const [isVisible, setIsVisible] = useState(false);
	const [animatedExp, setAnimatedExp] = useState(playerLevel?.exp ?? 0);
	const [animatedLevel, setAnimatedLevel] = useState(level);
	const [animatedProgress, setAnimatedProgress] = useState(levelProgress);
	const [isResetting, setIsResetting] = useState(false);

	const currentValuesRef = useRef({
		exp: playerLevel?.exp ?? 0,
		level: level,
		progress: levelProgress,
	});
	const prevValuesRef = useRef({
		exp: playerLevel?.exp ?? 0,
		level: level,
		progress: levelProgress,
	});
	const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
	const isAnimatingRef = useRef(false);
	const nodeRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!isAnimatingRef.current) {
			prevValuesRef.current = { ...currentValuesRef.current };
			currentValuesRef.current = {
				exp: playerLevel?.exp ?? 0,
				level: level,
				progress: levelProgress,
			};
		}
	}, [playerLevel?.exp, level, levelProgress]);

	useEffect(() => {
		timeoutRefs.current.forEach(clearTimeout);
		timeoutRefs.current = [];

		if (earnedExp && earnedExp > 0) {
			isAnimatingRef.current = true;

			const currentExp = playerLevel?.exp ?? 0;
			const currentLevel = level;
			const currentProgress = levelProgress;

			const prevExp = prevValuesRef.current.exp;
			const prevLevel = prevValuesRef.current.level;
			const prevProgress = prevValuesRef.current.progress;

			setAnimatedExp(prevExp);
			setAnimatedLevel(prevLevel);
			setAnimatedProgress(prevProgress);
			setIsResetting(false);
			setIsVisible(true);

			const showOldValuesTimeout = setTimeout(() => {
				if (currentLevel > prevLevel) {
					// LEVEL UP
					setAnimatedProgress(1);

					const levelChangeTimeout = setTimeout(() => {
						setIsResetting(true);
						setAnimatedProgress(0);
						setAnimatedLevel(currentLevel);
						setAnimatedExp(currentExp);

						const enableTransitionTimeout = setTimeout(() => {
							setIsResetting(false);

							const fillNewProgressTimeout = setTimeout(() => {
								setAnimatedProgress(currentProgress);

								const hideTimeout = setTimeout(() => {
									setIsVisible(false);
									isAnimatingRef.current = false;
									rage.player.clearLastEarnedExp();
								}, 5000);
								timeoutRefs.current.push(hideTimeout);
							}, 50);
							timeoutRefs.current.push(fillNewProgressTimeout);
						}, 50);
						timeoutRefs.current.push(enableTransitionTimeout);
					}, 800);
					timeoutRefs.current.push(levelChangeTimeout);
				} else {
					// БЕЗ LEVEL UP
					setAnimatedExp(currentExp);
					setAnimatedProgress(currentProgress);

					const waitTimeout = setTimeout(() => {
						setIsVisible(false);
						isAnimatingRef.current = false;
						rage.player.clearLastEarnedExp();
					}, 5000);
					timeoutRefs.current.push(waitTimeout);
				}
			}, 1000);
			timeoutRefs.current.push(showOldValuesTimeout);
		}

		return () => {
			timeoutRefs.current.forEach(clearTimeout);
			timeoutRefs.current = [];
		};
	}, [earnedExp, playerLevel?.exp, level, levelProgress]);

	useEffect(() => {
		if (!isAnimatingRef.current && !isVisible) {
			setAnimatedExp(playerLevel?.exp ?? 0);
			setAnimatedLevel(level);
			setAnimatedProgress(levelProgress);
		}
	}, [playerLevel?.exp, level, levelProgress, isVisible]);

	return (
		<CSSTransition
			in={isVisible}
			nodeRef={nodeRef}
			classNames="xp-info"
			timeout={700}
			unmountOnExit
		>
			<StyledContainer ref={nodeRef}>
				<StyledLevelSquare $side="left">
					<Text>{animatedLevel}</Text>
				</StyledLevelSquare>
				<StyledLevelProgressContainer>
					<StyledLevelProgressWrapper>
						<StyledLevelProgress
							$noTransition={isResetting}
							style={{ width: `${animatedProgress * 100}%` }}
						/>
					</StyledLevelProgressWrapper>
					<StyledLevelContainer>
						<img src={star} />
						<Text>
							<span>{animatedExp.toLocaleString('ru-RU')}</span> /{' '}
							{(playerLevel?.nextLevelExp ?? 0).toLocaleString('ru-RU')} EXP
						</Text>
					</StyledLevelContainer>
				</StyledLevelProgressContainer>
				<StyledLevelSquare $side="right">
					<Text>{animatedLevel + 1}</Text>
				</StyledLevelSquare>
			</StyledContainer>
		</CSSTransition>
	);
};

export { XPInfo };
